import React, { Component } from 'react';
import propTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import { connect } from 'react-redux';
import { withNavigation } from 'src/ui/hoc';

import { Card, CardContent, Columns, Column, MuiTitle } from 'src/ui/mui-ui-kit';;
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  PanelNavSlot,
  setBreadcrumbs,
  SaveChangesToast,
  ConfirmChangesModal,
  UiFieldset,
  UiInputAdapter,
  UiImageUploaderAdapter,
  UiToastAdapter,
  reduceServerErrors,
  InnerLoader,
  UiSelectAdapter,
} from 'src/ui';
import classNames from 'classnames';
import { Form, Field } from 'react-final-form';
import { PHONE_MASK } from 'src/ui/const';
import { toast } from 'react-toastify';
import { composeValidators, required, email } from 'src/ui/validations';
import { getFormData, fetchFormData } from 'src/account';
import _ from 'lodash';
import { addFI, getFIs, fetchFIs, updateFI } from '../redux';

class FIEditPage extends Component {
  state = {
    loading: true,
    title: '',
    confirmChangesModal: false,
    initialValues: {},
  };

  async componentDidMount() {
    const { setBreadcrumbs, match, fetchFIs, fetchFormData } = this.props;
    const { id } = match.params;

    const title = id ? 'Edit' : 'Add';

    this.setState({ title });

    setBreadcrumbs([
      { label: 'FIs', href: '/financial-institutions' },
      { label: title, href: match.url },
    ]);

    await Promise.all([fetchFormData(), fetchFIs()]);

    const { fis, formData } = this.props;
    if (fis.isError()) {
      reduceServerErrors(fis);
    } else {
      const initialValues = fis.items.find(fi => fi.id === +id) || {};
      this.setState({
        initialValues: {
          ...initialValues,
          institutionType: _.find(
            formData.financialInstitutionTypes,
            { id: initialValues.institutionType },
            null
          ),
          personPrimaryNumber: initialValues.personPrimaryNumber,
          supportPhone: initialValues.supportPhone,
        },
      });
    }

    this.setState({ loading: false });
  }

  handleSubmit = values => {
    const { match } = this.props;
    const { id } = match.params;

    if (id) {
      return this.updateFI(id, values);
    }
    return this.addFI(values);
  };

  showModal = () => {
    this.toggleModal(true);
  };

  hideModal = () => {
    this.toggleModal(false);
  };

  async updateFI(id, values) {
    const { updateFI, history } = this.props;
    const { logo, logoSmall, logoBlue, logoMail, ...rest } = values;

    await updateFI({
      financialInstitutionId: id,
      ...rest,
      institutionType: values.institutionType.id,
      personPrimaryNumber: rest.personPrimaryNumber,
      supportPhone: rest.supportPhone,
      logo: typeof logo === 'string' ? null : logo,
      logoSmall: typeof logoSmall === 'string' ? null : logoSmall,
      logoBlue: typeof logoBlue === 'string' ? null : logoBlue,
      logoMail: typeof logoMail === 'string' ? null : logoMail,
    });

    const { fis } = this.props;
    if (fis.isError()) {
      return reduceServerErrors(fis);
    }

    toast(
      UiToastAdapter('Financial Institution updated successfully!', 'success')
    );
    history.push('/financial-institutions');
    return undefined;
  }

  async addFI(values) {
    const { addFI, history } = this.props;

    await addFI({
      ...values,
      institutionType: values.institutionType.id,
      supportPhone: values.supportPhone,
      allowsBanking: true,
      allowsPayments: true,
    });

    const { fis } = this.props;
    if (fis.isError()) {
      return reduceServerErrors(fis);
    }

    toast(
      UiToastAdapter('Financial Institution added successfully!', 'success')
    );
    history.push('/financial-institutions');

    return undefined;
  }

  toggleModal(value) {
    this.setState({ confirmChangesModal: value });
  }

  render() {
    const { isExpanded, formData } = this.props;
    const { title, confirmChangesModal, loading, initialValues } = this.state;

    const toastContainerClassName = classNames('bottom-toast-container', {
      'is-condensed': !isExpanded,
    });

    return (
      <>
        <PanelNavSlot>
          <NavLink
            className="close-page button is-small is-text"
            to="/financial-institutions"
          >
            <span className="icon has-text-danger">
              <FontAwesomeIcon icon={['fal', 'times-circle']} size="2x" />
            </span>
          </NavLink>
        </PanelNavSlot>
        <MuiTitle isSize="4" hasTextColor="info" tag="h2">
          {`${title} Financial Institution`}
        </MuiTitle>
        <Form
          onSubmit={this.handleSubmit}
          initialValues={initialValues}
          subscription={{
            submitting: true,
            dirty: true,
            hasValidationErrors: true,
            dirtySinceLastSubmit: true,
          }}
          render={({
            handleSubmit,
            submitting,
            hasValidationErrors,
            dirty,
            dirtySinceLastSubmit,
          }) => (
            <Card tag="form" onSubmit={handleSubmit} noValidate>
              <CardContent>
                <div className={toastContainerClassName}>
                  <SaveChangesToast
                    message="Click the button to the right to save your changes"
                    isLoading={submitting}
                    isDisabled={submitting || hasValidationErrors}
                    isVisible={dirty || dirtySinceLastSubmit}
                    onClickButton={this.showModal}
                  />
                </div>
                <ConfirmChangesModal
                  title="Financial Institutions"
                  isActive={confirmChangesModal}
                  onClickCancel={this.hideModal}
                  onClickSubmit={this.hideModal}
                />
                <InnerLoader visible={submitting || loading} />
                <UiFieldset title="Primary data" isFirst>
                  <Columns isMultiline>
                    <Column isSize="1/2">
                      <Field
                        component={UiInputAdapter}
                        name="businessName"
                        id="businessName"
                        required
                        label="Business Name"
                        validate={composeValidators('Business Name', required)}
                      />
                    </Column>
                    <Column isSize="1/2">
                      <Field
                        component={UiSelectAdapter}
                        name="institutionType"
                        id="institutionType"
                        required
                        label="Institution Type"
                        validate={composeValidators(
                          'Institution Type',
                          required
                        )}
                        options={formData.financialInstitutionTypes}
                        getOptionLabel={item => item.name}
                        getOptionValue={item => item.id}
                      />
                    </Column>
                  </Columns>
                </UiFieldset>

                <UiFieldset title="Administrator">
                  <Columns isMultiline>
                    <Column isSize="1/2">
                      <Field
                        component={UiInputAdapter}
                        name="personFirstName"
                        id="personFirstName"
                        required
                        label="First Name"
                        validate={composeValidators('First Name', required)}
                      />
                    </Column>
                    <Column isSize="1/2">
                      <Field
                        component={UiInputAdapter}
                        name="personLastName"
                        id="personLastName"
                        required
                        label="Last Name"
                        validate={composeValidators('Last Name', required)}
                      />
                    </Column>

                    <Column isSize="1/2">
                      <Field
                        component={UiInputAdapter}
                        name="personEmailAddress"
                        id="personEmailAddress"
                        required
                        label="E-mail"
                        type="email"
                        validate={composeValidators('E-mail', required, email)}
                      />
                    </Column>
                    <Column isSize="1/2">
                      <Field
                        component={UiInputAdapter}
                        name="personPrimaryNumber"
                        id="personPrimaryNumber"
                        required
                        label="Phone"
                        mask={PHONE_MASK}
                        type="tel"
                        validate={composeValidators('Phone', required)}
                      />
                    </Column>
                  </Columns>
                </UiFieldset>

                <UiFieldset title="Support contact">
                  <Columns isMultiline>
                    <Column isSize="1/2">
                      <Field
                        component={UiInputAdapter}
                        name="supportName"
                        id="supportName"
                        required
                        label="Support Name"
                        validate={composeValidators('Support Name', required)}
                      />
                    </Column>
                    <Column isSize="1/2">
                      <Field
                        component={UiInputAdapter}
                        name="supportPhone"
                        id="supportPhone"
                        required
                        label="Support Phone"
                        mask={PHONE_MASK}
                        type="tel"
                        validate={composeValidators('Support Phone', required)}
                      />
                    </Column>
                  </Columns>
                </UiFieldset>

                <UiFieldset title="Logos">
                  <Columns isMultiline>
                    <Column isSize="1/2">
                      <Field
                        component={UiImageUploaderAdapter}
                        name="logo"
                        id="logo"
                        required
                        label="Main logo"
                        type="file"
                      />
                    </Column>
                    <Column isSize="1/2">
                      <Field
                        component={UiImageUploaderAdapter}
                        name="logoSmall"
                        id="logoSmall"
                        required
                        label="Small logo"
                        type="file"
                      />
                    </Column>
                    <Column isSize="1/2">
                      <Field
                        component={UiImageUploaderAdapter}
                        name="logoBlue"
                        id="logoBlue"
                        required
                        label="Blue logo"
                        type="file"
                      />
                    </Column>
                    <Column isSize="1/2">
                      <Field
                        component={UiImageUploaderAdapter}
                        name="logoMail"
                        id="logoMail"
                        required
                        label="E-Mail logo"
                        type="file"
                      />
                    </Column>
                  </Columns>
                </UiFieldset>
              </CardContent>
            </Card>
          )}
        />
      </>
    );
  }
}

FIEditPage.propTypes = {
  setBreadcrumbs: propTypes.func.isRequired,
  addFI: propTypes.func.isRequired,
  fetchFIs: propTypes.func.isRequired,
  updateFI: propTypes.func.isRequired,
  fetchFormData: propTypes.func.isRequired,
  match: propTypes.object.isRequired,
  history: propTypes.object.isRequired,
  fis: propTypes.object.isRequired,
  formData: propTypes.object.isRequired,
  isExpanded: propTypes.bool.isRequired,
};

function mapStateToProps(state) {
  return {
    fis: getFIs(state),
    formData: getFormData(state),
  };
}

const connectedFIEditPage = connect(mapStateToProps, {
  setBreadcrumbs,
  addFI,
  fetchFIs,
  updateFI,
  fetchFormData,
})(withNavigation(FIEditPage));

export { connectedFIEditPage as FIEditPage };
